import React, { useEffect, useRef, useReducer } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { wrapFunctionalComponent } from 'utils/framework';
import UtilActions from 'utils/redux/Actions';
import addToBasketActions from 'actions/AddToBasketActions';
import anaConsts from 'analytics/constants';
import * as pdpAnalytics from 'analytics/productPage/pdpAnalytics';
import { Divider, Box } from 'components/ui';
import CallToAction from 'components/ProductPage/CallToAction';
import ProductLoveButton from 'components/Product/ProductLove/ProductLoveButton/ProductLoveButton';
import CustomSetItem from 'components/ProductPage/CustomSets/CustomSetItem/CustomSetItem';
import mediaUtils from 'utils/Media';
import ProductLove from 'components/Product/ProductLove';
import actions from 'actions/Actions';
import { breakpoints } from 'style/config';

const { Media } = mediaUtils;

const getInitialState = ({ currentSku }) => {
    const { configurableOptions = {}, skuId } = currentSku;
    const { groupedSkuOptions = [] } = configurableOptions;

    const skuOptions = groupedSkuOptions.map(groupedSkuOption => {
        delete groupedSkuOption.choiceSelected;
        groupedSkuOption.selectedSku = groupedSkuOption.skuOptions[0];
        groupedSkuOption.selectedSku.primaryProduct = {
            brand: {
                displayName: groupedSkuOption.groupProduct.brand.displayName
            },
            displayName: groupedSkuOption.groupProduct.displayName
        };
        groupedSkuOption.isExpanded = false;

        return groupedSkuOption;
    });

    return { lastSelectedSku: skuOptions[0]?.selectedSku, skuId, skuOptions };
};

function customSetsReducer(state, action) {
    switch (action.type) {
        case 'RESET':
            return getInitialState(action);
        case 'SET_SKU_OPTIONS':
            return { ...state, skuOptions: action.skuOptions };
        case 'SET_SKU_OPTIONS_AND_LAST':
            return { ...state, skuOptions: action.skuOptions, lastSelectedSku: action.lastSelectedSku };
        default:
            return state;
    }
}

function fireAnalytics(action, value, skuId) {
    const eventName = action === 'select' ? anaConsts.Event.SC_ADD : anaConsts.Event.SC_REMOVE;
    pdpAnalytics.trackLinkClick({
        linkName: 'D=c55',
        actionInfo: value,
        eventStrings: [anaConsts.Event.EVENT_71, eventName],
        sku: { skuId }
    });
}

// eslint-disable-next-line complexity
const CustomSets = ({
    addToBasketDataAt, addToBasketDataAtSm, isRopis, isSkuReady, currentSku, currentProduct, analyticsContext
}) => {
    const dispatch = useDispatch();
    const [localState, localDispatch] = useReducer(customSetsReducer, undefined, () => getInitialState({ currentSku }));

    const shoppingListIds = useSelector(state => state.loves?.shoppingListIds, shallowEqual) || [];
    const currentSkuRef = useRef(currentSku);
    currentSkuRef.current = currentSku;
    const currentProductRef = useRef(currentProduct);
    currentProductRef.current = currentProduct;

    useEffect(() => {
        localDispatch({ type: 'RESET', currentSku });
    }, [currentSku.skuId]); // eslint-disable-line react-hooks/exhaustive-deps

    const mergeChoicesStore = skuOptions => {
        const choices = skuOptions.filter(sku => sku.choiceSelected).map(sku => ({ skuId: sku.choiceSelected.skuId, qty: 1 }));
        dispatch(UtilActions.merge('page.product', 'customSetsChoices', choices));
    };

    const selectSkuChoice = skuIndex => {
        const skuOptions = localState.skuOptions.slice();
        skuOptions[skuIndex].choiceSelected = skuOptions[skuIndex].selectedSku;
        localDispatch({ type: 'SET_SKU_OPTIONS', skuOptions });
        mergeChoicesStore(skuOptions);
        fireAnalytics('select', 'product:custom set:select', skuOptions[skuIndex].choiceSelected.skuId);
    };

    const removeSkuChoice = skuIndex => {
        const skuOptions = localState.skuOptions.slice();
        const deletedSkuId = skuOptions[skuIndex].choiceSelected.skuId;
        delete skuOptions[skuIndex].choiceSelected;
        localDispatch({ type: 'SET_SKU_OPTIONS', skuOptions });
        mergeChoicesStore(skuOptions);
        fireAnalytics('remove', 'product:custom set:remove', deletedSkuId);
    };

    const updateSkuImage = (updatedSku, skuIndex) => {
        const skuOptions = localState.skuOptions.slice();
        skuOptions[skuIndex].selectedSku = skuOptions[skuIndex].skuOptions.find(sku => sku.skuId === updatedSku.skuId);
        localDispatch({ type: 'SET_SKU_OPTIONS_AND_LAST', skuOptions, lastSelectedSku: skuOptions[skuIndex].selectedSku });
    };

    const showAddToBasketModal = () => {
        const prod = currentProductRef.current;
        dispatch(
            actions.showAddToBasketModal({
                isOpen: true,
                product: prod,
                sku: prod.currentSku,
                quantity: prod.currentSkuQuantity,
                analyticsContext
            })
        );
    };

    const addAllToBasket = () => {
        const prod = currentProductRef.current;
        const sku = currentSkuRef.current;

        const analyticsData = {
            productName: prod.productDetails.displayName,
            skuType: prod.type,
            productId: prod.productDetails.productId
        };

        const skusToAdd = [
            {
                productId: prod.productDetails.productId,
                ...prod.customSetsChoices.slice()[0]
            }
        ];
        skusToAdd.push({ skuId: sku.skuId, productId: prod.productDetails.productId, qty: 1 });

        dispatch(
            addToBasketActions.addMultipleSkusToBasket(
                skusToAdd,
                skusToAdd.length,
                () => {
                    dispatch(UtilActions.merge('page.product', 'customSetsChoices', []));
                    localDispatch({ type: 'RESET', currentSku: currentSkuRef.current });
                },
                null,
                analyticsData,
                sku
            )
        );

        if (window.matchMedia(breakpoints.smMin).matches) {
            showAddToBasketModal();
        }
    };

    const { lastSelectedSku, skuOptions } = localState;
    const { customSetsChoices = [] } = currentProduct;
    const isCurrentSkuLoved = !!shoppingListIds.find(skuId => skuId === currentSku.skuId);

    const loveButton = (
        <ProductLove
            sku={currentSku}
            customSetsChoices={customSetsChoices}
            loveSource='productPage'
            productId={currentProduct?.productDetails?.productId}
        >
            <ProductLoveButton
                isCustomSetsProduct={true}
                customSetsChoice={customSetsChoices}
                currentProduct={currentProduct}
                sku={currentSku}
                loveSource='productPage'
                disabled={customSetsChoices.length === 0 && !isCurrentSkuLoved}
            />
        </ProductLove>
    );

    return (
        <React.Fragment>
            {skuOptions.length > 0 ? (
                <Box
                    data-at={Sephora.debug.dataAt('pdp_custom_set_block')}
                    paddingTop={[4, null, 0]}
                    marginBottom={6}
                >
                    <Media greaterThan='sm'>
                        <Divider
                            marginTop={4}
                            marginBottom={3}
                        />
                    </Media>
                    {skuOptions.map((sku, skuIndex) => (
                        <React.Fragment key={`customSetItem_${sku.selectedSku.skuId}`}>
                            {skuIndex > 0 && (
                                <Divider
                                    height={3}
                                    marginX={[-2, null, 0]}
                                    marginY={3}
                                />
                            )}
                            <CustomSetItem
                                isSkuReady={isSkuReady}
                                skuIndex={skuIndex}
                                sku={sku}
                                currentProduct={currentProduct}
                                selectSkuChoice={selectSkuChoice}
                                removeSkuChoice={removeSkuChoice}
                                updateSkuImage={updateSkuImage}
                            />
                        </React.Fragment>
                    ))}
                </Box>
            ) : null}

            {isSkuReady && <Media lessThan='md'>{loveButton}</Media>}

            <CallToAction
                isCustomSets={true}
                addToBasketDataAt={addToBasketDataAt}
                addToBasketDataAtSm={addToBasketDataAtSm}
                isRopis={isRopis}
                sku={lastSelectedSku}
                product={currentProduct}
                loveButton={isSkuReady && loveButton}
                addToBasketCallback={addAllToBasket}
            />
        </React.Fragment>
    );
};

export default wrapFunctionalComponent(CustomSets, 'CustomSets');
